import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius } from '@liminal/brand/scale';
import { Card } from '@liminal/web-ui/primitives/Card';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import type { Schemas } from '@/api/generated';

interface MerchantGalleryProps {
  /** As the server holds them: in display order, the main photo first. */
  readonly images: readonly Schemas['RewardMerchantImage'][];
}

/** A partner's photos on its detail page, main first and larger, as the app leads with it. */
export function MerchantGallery({ images }: MerchantGalleryProps) {
  return (
    <Card variant="elevated">
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Photos</Typography>
          <Typography variant="body2" color="text.secondary">
            The first is the main photo, shown on the partner&apos;s card in the app.
          </Typography>
        </Stack>
        {images.length === 0 ? (
          <EmptyState icon="image" title="No photos yet" subtitle="Add them with Edit." size="sm" />
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
            {images.map((image, index) => {
              if (!image.name || !image.uri) throw new Error(`a partner image came back without its name or URI: ${JSON.stringify(image)}`);
              const main = index === 0;
              return (
                <Box
                  key={image.name}
                  sx={{ position: 'relative', gridColumn: main ? 'span 2' : undefined, gridRow: main ? 'span 2' : undefined }}
                >
                  <Box
                    component="img"
                    src={image.uri}
                    alt={main ? 'Main photo' : `Photo ${index + 1}`}
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      aspectRatio: '4 / 3',
                      objectFit: 'cover',
                      borderRadius: `${borderRadius.md}px`,
                      border: `1px solid ${webColors.border.light}`,
                    }}
                  />
                  {main && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                      <StatusPill tone="info" label="Main" size="sm" />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Stack>
    </Card>
  );
}
